import React, { useRef, useEffect, useState, useMemo } from "react";
import { CryptoAlertData, PositionData } from "@crypto-trading/common/browser";
import { TrendingUp, TrendingDown, X } from "lucide-react";
import { PositionChart } from "@/components/PositionChart";
import {
  TradingViewChartWidget,
} from "@crypto-trading/ui-shared/client";
import { useAlertPriceData } from "@/hooks/useAlertPriceData";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import { TokenAnalysisSection } from "@/components/TokenAnalysisSection";
import { trackEvent } from "@/utils/analytics";
import { useTokenAvailability } from "@/hooks/useMarketData";
import { useUserExchanges } from "@/hooks/useExchanges";
import { ensureUsdtSymbol } from "@/utils/symbols";
import { usePreviousAlerts } from "@/hooks/useCryptoAlerts";
import { TokenIcon } from "@/components/TokenIcon";
import { AlertHistory } from "@/components/AlertHistory";

interface AlertDetailDrawerDesktopProps {
  userId: string;
  alert: CryptoAlertData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTrade?: (alert: CryptoAlertData) => void;
  onViewPosition?: (position: PositionData) => void;
}

const SHEET_WIDTH = 500;

export const AlertDetailDrawerDesktop: React.FC<
  AlertDetailDrawerDesktopProps
> = ({ userId, alert, open, onOpenChange, onTrade, onViewPosition }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const [isLivePrice, setIsLivePrice] = useState(false);

  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const isListing = alert?.type === "new_coin";

  // Normalize symbol for token availability check
  const normalizedSymbol = useMemo(
    () => (alert?.currency ? ensureUsdtSymbol(alert.currency) : ""),
    [alert?.currency]
  );

  // Check token availability and user exchanges
  const { data: exchanges = [], isLoading: isLoadingExchanges } =
    useUserExchanges(userId, true);
  const { data: tokenAvailability, isLoading: isLoadingAvailability } =
    useTokenAvailability(userId, normalizedSymbol, open && !!normalizedSymbol);

  const globallyAvailableExchangeCodes = useMemo(
    () => tokenAvailability?.userExchanges ?? [],
    [tokenAvailability?.userExchanges]
  );
  const globallyAvailableSet = useMemo(
    () => new Set(globallyAvailableExchangeCodes),
    [globallyAvailableExchangeCodes]
  );
  const supportedUserExchangeCodes = useMemo(
    () =>
      exchanges
        .map((e) => e.code)
        .filter((code) => globallyAvailableSet.has(code)),
    [exchanges, globallyAvailableSet]
  );
  const hasTradableExchange = supportedUserExchangeCodes.length > 0;
  const isCheckingAvailability = isLoadingAvailability || isLoadingExchanges;

  // Fetch previous alerts
  const { data: previousAlerts = [] } = usePreviousAlerts(
    userId,
    alert?.currency || "",
    alert?.id || "",
    open && !!alert
  );

  // Subscribe to live price updates
  const { price: livePrice, isSubscribed } = useTokenLivePrice(
    alert?.currency || "",
    "dynamic",
    open && isListing && !!alert
  );

  const [priceLoadTimeout, setPriceLoadTimeout] = useState(false);

  useEffect(() => {
    if (open && isListing && !livePrice) {
      setPriceLoadTimeout(false);
      const timer = setTimeout(() => setPriceLoadTimeout(true), 8000);
      return () => clearTimeout(timer);
    }
  }, [open, isListing, livePrice]);

  const displayPrice = livePrice ?? null;

  const {
    enabledIntervals,
    selectedInterval,
    setSelectedInterval,
    priceChanges,
    isLoading,
    isLoadingMore,
    error: candleError,
    loadOlderCandles,
    loadNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    setFollowLive,
    symbol,
    isTokenUnavailable,
  } = useAlertPriceData({ userId, alert, open });

  const isPriceLoading =
    open && isListing && !livePrice && !priceLoadTimeout && !isTokenUnavailable;

  useEffect(() => {
    if (!open) {
      setIsLivePrice(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && alert) {
      trackEvent("alert_drawer_view", {
        userId,
        alertId: alert.id,
        symbol: alert.currency,
        alertType: alert.type,
        tier: alert.tier ?? null,
        exchange: alert.exchange,
      });
    }
  }, [open, alert]);

  useEffect(() => {
    if (scrollContainerRef.current && scrollPositionRef.current > 0) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, [selectedInterval, isLoading, priceChanges.length]);

  // Animation effect
  useEffect(() => {
    if (open && alert) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      document.body.style.overflow = "hidden";
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      document.body.style.overflow = "";
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, alert]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onOpenChange(false);
    };
    if (open) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onOpenChange]);

  if (!alert || !isVisible) return null;

  const formatPrice = (price: number | null) => {
    if (!price)
      return isTokenUnavailable
        ? "Not available at supported exchanges"
        : "Not available";
    return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
  };

  const calculatePriceChange = () => {
    if (!alert?.price || !displayPrice) return null;
    return ((displayPrice - alert.price) / alert.price) * 100;
  };

  const formatDateTime = (date: Date) => {
    const d = new Date(date);
    return d.toLocaleString(undefined, {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h24",
    });
  };

  const priceChange = calculatePriceChange();
  const tierBadgeColor =
    alert.tier === 1
      ? "text-yellow-400"
      : alert.tier === 2
        ? "text-blue-400"
        : "text-gray-400";

  const handleViewPosition = () => {
    if (alert.userPosition && onViewPosition) {
      const positionData: PositionData = {
        positionId: alert.userPosition.id,
        symbol: alert.userPosition.symbol,
        positionType: alert.userPosition.positionType as "LONG" | "SHORT",
        amount: alert.userPosition.amount.toString(),
        leverage: alert.userPosition.leverage.toString(),
        entryPrice: alert.userPosition.entryPrice.toString(),
        markPrice: alert.userPosition.markPrice?.toString() || "0",
        exitPrice: alert.userPosition.exitPrice?.toString() || "0",
        pnl: alert.userPosition.realizedPnl?.toString() || "0",
        pnlPercentage: alert.userPosition.pnlPercentage?.toString() || "0",
        exchangeCode: alert.exchange,
        exchangeName: alert.exchange,
        usdAmount: alert.userPosition.usdAmount.toString(),
        openedAt: alert.userPosition.openedAt?.toString() || undefined,
        closedAt: alert.userPosition.closedAt?.toString() || undefined,
      };
      onViewPosition(positionData);
    }
  };

  return (
    <>
      {/* Overlay with fade animation */}
      <div
        className="fixed inset-0 bg-black/60 z-[100] transition-opacity duration-300"
        style={{ opacity: isAnimating ? 1 : 0 }}
        onClick={() => onOpenChange(false)}
      />

      {/* Sheet with slide animation */}
      <div
        className="fixed top-0 right-0 h-full bg-secondary border-l border-border flex flex-col shadow-2xl z-[101] transition-transform duration-300 ease-out"
        style={{
          width: `${SHEET_WIDTH}px`,
          transform: isAnimating ? "translateX(0)" : "translateX(100%)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            {isListing ? (
              <TrendingUp className="w-5 h-5 text-green-400" />
            ) : (
              <TrendingDown className="w-5 h-5 text-red-400" />
            )}
            <h2 className="text-lg font-semibold text-text-primary">
              {alert.currency}
            </h2>
            <TokenIcon
              symbol={alert.currency}
              logo={alert.tokenMetadata?.logo}
              size="sm"
            />
            <span className="px-2 py-0.5 rounded-full text-xs bg-black/50">
              {alert.type === "new_coin" ? "Listing" : "Delisting"}
            </span>
            {alert.hasAutotraded && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-accent/10">
                Autotraded
              </span>
            )}
            {alert.tier && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs bg-black/50 ${tierBadgeColor}`}
              >
                Tier {"🔥".repeat(4 - alert.tier)}
              </span>
            )}
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="p-2 rounded-lg hover:bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-6"
        >
          {/* Alert Info Grid */}
          <div className="space-y-4 p-3 bg-tertiary/50 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-text-secondary mb-1">Exchange</p>
                <p className="text-sm text-text-primary capitalize">
                  {alert.exchange}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-secondary mb-1">
                  Exchange Type
                </p>
                <p className="text-sm text-text-primary capitalize">
                  {alert.exchangeType || "spot"}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-text-secondary mb-1">Alert Price</p>
                <p className="text-sm text-text-primary">
                  {formatPrice(alert.price)}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <p className="text-xs text-text-secondary">Current Price</p>
                  {isSubscribed && livePrice && (
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                  )}
                </div>
                {isPriceLoading ? (
                  <div className="h-5 bg-gray-600/30 rounded animate-pulse w-24"></div>
                ) : (
                  <>
                    <p className="text-sm text-text-primary">
                      {formatPrice(displayPrice)}
                    </p>
                    {priceChange !== null && (
                      <p
                        className={`text-xs mt-0.5 ${priceChange >= 0 ? "text-green-400" : "text-red-400"}`}
                      >
                        {priceChange >= 0 ? "+" : ""}
                        {priceChange.toFixed(2)}%
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {alert.source && (
                <div>
                  <p className="text-xs text-text-secondary mb-1">Meta</p>
                  <a
                    href={alert.source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-accent hover:underline underline"
                  >
                    Source
                  </a>
                </div>
              )}
              <div>
                <p className="text-xs text-text-secondary mb-1">Alert Time</p>
                <p className="text-sm text-text-primary">
                  {formatDateTime(alert.createdAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Price Chart */}
          <div>
            {isLivePrice ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-text-primary">
                    Price Chart
                  </h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-xs text-text-secondary">Live</span>
                    <div className="relative inline-block w-10 h-5">
                      <input
                        type="checkbox"
                        checked={isLivePrice}
                        onChange={(e) => setIsLivePrice(e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        className={`block w-10 h-5 rounded-full transition-colors ${isLivePrice ? "bg-accent" : "bg-gray-600"}`}
                      />
                      <div
                        className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform ${isLivePrice ? "translate-x-5" : "translate-x-0"}`}
                      />
                    </div>
                  </label>
                </div>
                <TradingViewChartWidget symbol={symbol} />
              </>
            ) : isLoading || priceChanges.length > 0 || candleError ? (
              <PositionChart
                priceChanges={priceChanges}
                entryPrice={alert.price}
                isProfitable={
                  alert.userPosition?.pnlPercentage !== null &&
                  alert.userPosition?.pnlPercentage !== undefined &&
                  alert.userPosition.pnlPercentage >= 0
                }
                openedAt={alert.createdAt.toString()}
                markerText="Triggered"
                entryMarkerVariant="callout"
                onRequestOlderCandles={loadOlderCandles}
                onRequestNewerCandles={loadNewerCandles}
                hasMoreHistory={hasMoreHistory}
                hasMoreFuture={hasMoreFuture}
                onFollowLiveChange={setFollowLive}
                viewportResetKey={`${selectedInterval}-${alert?.id ?? ""}`}
                enabledIntervals={enabledIntervals}
                selectedInterval={selectedInterval}
                onIntervalChange={setSelectedInterval}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                error={candleError}
                headerActions={
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-xs text-text-secondary">Live</span>
                    <div className="relative inline-block w-10 h-5">
                      <input
                        type="checkbox"
                        checked={isLivePrice}
                        onChange={(e) => setIsLivePrice(e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        className={`block w-10 h-5 rounded-full transition-colors ${isLivePrice ? "bg-accent" : "bg-gray-600"}`}
                      />
                      <div
                        className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform ${isLivePrice ? "translate-x-5" : "translate-x-0"}`}
                      />
                    </div>
                  </label>
                }
              />
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-text-primary">
                    Price Chart
                  </h3>
                  <span className="text-xs text-text-secondary">Live</span>
                </div>
                <TradingViewChartWidget symbol={symbol} />
              </>
            )}
          </div>

          {/* Previous Alerts Section */}
          <AlertHistory alerts={previousAlerts} variant="drawer-desktop" />

          {/* Token Metadata Section */}
          {alert.tokenMetadata &&
            (alert.tokenMetadata.description || alert.tokenMetadata.urls) && (
              <div className="space-y-3 bg-primary/40 border border-gray-500/30 rounded-lg p-3">
                <h3 className="text-sm font-bold text-text-primary">
                  Token Information
                </h3>
                {alert.tokenMetadata.description && (
                  <div>
                    <p className="text-xs text-text-secondary mb-1">
                      Description
                    </p>
                    <p className="text-sm text-text-primary leading-relaxed">
                      {alert.tokenMetadata.description}
                    </p>
                  </div>
                )}
                {alert.tokenMetadata.urls && (
                  <div>
                    <p className="text-xs text-text-secondary mb-2">
                      Official Links
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {alert.tokenMetadata.urls.website?.[0] && (
                        <a
                          href={alert.tokenMetadata.urls.website[0]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors"
                        >
                          Website
                        </a>
                      )}
                      {alert.tokenMetadata.urls.twitter?.[0] && (
                        <a
                          href={alert.tokenMetadata.urls.twitter[0]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors"
                        >
                          Twitter
                        </a>
                      )}
                      {alert.tokenMetadata.urls.source_code?.[0] && (
                        <a
                          href={alert.tokenMetadata.urls.source_code[0]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors"
                        >
                          GitHub
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

          <TokenAnalysisSection tokenSymbol={alert.currency} />
        </div>

        {/* Footer Actions */}
        {(alert.userPosition ||
          (onTrade && (hasTradableExchange || isCheckingAvailability))) && (
          <div className="sticky bottom-0 bg-secondary border-t border-border p-4">
            {alert.userPosition && (
              <button
                type="button"
                onClick={handleViewPosition}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                View Position
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
